import React, { useState } from 'react'
import styled from 'styled-components'
import Glp1Button from './Glp1Button'

interface PartnerOption {
  id: string
  logo: React.ReactNode
  title: string
  bulletPoints: string[]
  ctaLabel: string
  ctaUrl: string
}

type ModalBlock = {
  __typename: 'ModalBlockRecord'
  icon: {
    url: string
  } | null
  body: string | null
  button: {
    __typename: 'CtaBlockRecord'
    buttonText: string | null
    url: string | null
  } | null
}

interface DanishPartnerModalProps {
  isOpen: boolean
  onClose: () => void
  options?: PartnerOption[]
  modals?: ModalBlock[]
}

const Overlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  padding: 22px;
`

const ModalContainer = styled.div<{ $hasExpanded: boolean }>`
  background-color: white;
  width: 88%;
  max-width: 600px;
  padding: 22px;
  border-radius: 8px;
  position: relative;
  z-index: 1001;
  min-height: ${({ $hasExpanded }) => ($hasExpanded ? '320px' : 'auto')};
  display: flex;
  flex-direction: column;
  transition: min-height 0.3s ease;

  @media (min-width: 768px) {
    min-height: ${({ $hasExpanded }) => ($hasExpanded ? '280px' : 'auto')};
  }
`

const OptionContainer = styled.div<{
  $isExpanded: boolean
  $isSelected: boolean
}>`
  min-height: 60px;
  max-height: ${({ $isExpanded }) => ($isExpanded ? '1000px' : '60px')};
  padding: 12px;
  border-radius: 8px;
  border: ${({ $isSelected }) =>
    $isSelected ? 'none' : '1px solid rgba(0, 0, 0, 0.30)'};
  background-color: ${({ $isSelected }) =>
    $isSelected ? '#DFFBFF' : 'transparent'};
  margin-bottom: 12px;
  cursor: pointer;
  transition:
    max-height 0.3s ease,
    background-color 0.3s ease,
    border 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`

const OptionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 36px;
`

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`

const IconButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #0000004d;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  padding: 0;
`

const PlusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M8 3V13M3 8H13"
      stroke="black"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
)

const MinusIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M3 8H13" stroke="black" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const ExpandedContent = styled.div`
  margin-top: 16px;
`

const BulletList = styled.ul`
  list-style: none;
  padding-left: 16px;
  margin-botton: 20px;
`

const BulletItem = styled.li`
  font-size: 17px;
  font-style: normal;
  font-weight: 450;
  line-height: 21px;
  letter-spacing: -0.17px;
  padding-left: 20px;
  position: relative;

  &::before {
    content: '•';
    position: absolute;
    left: 0;
    color: #000;
  }
`

const CtaContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 20px;
`

// Logo components - replace with actual logos when available
const _FalckLogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const _FalckText = styled.span`
  font-weight: bold;
  font-size: 24px;
  color: #d32f2f;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const _YazenLogoContainer = styled.div`
  display: flex;
  align-items: center;
`

const _YazenText = styled.span`
  font-weight: 600;
  font-size: 28px;
  color: #000;
  letter-spacing: -0.5px;

  &::first-letter {
    font-size: 32px;
  }
`

const IconImage = styled.img`
  max-height: 40px;
  max-width: 120px;
  object-fit: contain;
`

const IconText = styled.span`
  font-weight: 600;
  font-size: 24px;
  color: #000;
`

const parseBulletPoints = (body: string | null): string[] => {
  if (!body) return []

  const bulletRegex = /^[-*]\s+(.+)$/gm
  const matches = body.match(bulletRegex)

  if (matches && matches.length > 0) {
    return matches.map((match) => match.replace(/^[-*]\s+/, '').trim())
  }

  const htmlListRegex = /<li[^>]*>(.*?)<\/li>/gi
  const htmlMatches = body.match(htmlListRegex)

  if (htmlMatches && htmlMatches.length > 0) {
    return htmlMatches.map((match) => match.replace(/<[^>]*>/g, '').trim())
  }

  const lines = body
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0)

  return lines.length > 0 ? lines : [body]
}

const transformModalsToOptions = (modals: ModalBlock[]): PartnerOption[] => {
  return modals
    .filter((modal) => {
      return modal != null && modal.__typename === 'ModalBlockRecord'
    })
    .map((modal, index) => {
      const id = `modal-${index}`
      const iconUrl = modal.icon?.url

      const logo = iconUrl ? (
        <IconImage src={iconUrl} alt="" />
      ) : (
        <IconText>Partner {index + 1}</IconText>
      )

      const bulletPoints = parseBulletPoints(modal.body || null)
      const ctaLabel = modal.button?.buttonText || 'Learn More'
      const ctaUrl = modal.button?.url || '#'

      return {
        id,
        logo,
        title: iconUrl || `Partner ${index + 1}`,
        bulletPoints,
        ctaLabel,
        ctaUrl,
      }
    })
}

const DanishPartnerModal: React.FC<DanishPartnerModalProps> = ({
  isOpen,
  onClose,
  options,
  modals,
}) => {
  const [selectedOption, setSelectedOption] = useState<string | null>(null)

  const handleOptionClick = (optionId: string) => {
    if (selectedOption === optionId) {
      setSelectedOption(null)
    } else {
      setSelectedOption(optionId)
    }
  }

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose()
      setSelectedOption(null)
    }
  }

  if (!isOpen) return null

  const displayOptions = modals
    ? transformModalsToOptions(modals)
    : options || []

  if (displayOptions.length === 0) {
    return null
  }

  const hasExpanded = selectedOption !== null

  return (
    <Overlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer
        $hasExpanded={hasExpanded}
        onClick={(e) => e.stopPropagation()}
      >
        {displayOptions.map((option) => {
          const isExpanded = selectedOption === option.id
          return (
            <OptionContainer
              key={option.id}
              $isExpanded={isExpanded}
              $isSelected={isExpanded}
              onClick={() => handleOptionClick(option.id)}
            >
              <OptionHeader>
                <LogoContainer>{option.logo}</LogoContainer>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    handleOptionClick(option.id)
                  }}
                >
                  {isExpanded ? <MinusIcon /> : <PlusIcon />}
                </IconButton>
              </OptionHeader>
              {isExpanded && (
                <ExpandedContent>
                  <BulletList>
                    {option.bulletPoints.map((point, index) => (
                      <BulletItem key={index}>{point}</BulletItem>
                    ))}
                  </BulletList>
                  <CtaContainer>
                    <Glp1Button
                      href={option.ctaUrl}
                      isSimple={true}
                      onClick={() => {}}
                    >
                      {option.ctaLabel}
                    </Glp1Button>
                  </CtaContainer>
                </ExpandedContent>
              )}
            </OptionContainer>
          )
        })}
      </ModalContainer>
    </Overlay>
  )
}

export default DanishPartnerModal
